import { useRef, useState, useEffect } from "react";
import html2pdf from "html2pdf.js";
import {
    Box,
    Flex,
    Text,
    Image,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
} from "@chakra-ui/react";
import DOMPurify from 'dompurify';
import downloadIcon from "common/assets/img/download.png";
import eyeIcon from "common/assets/img/eye.png";
import { globalStyles } from "common/theme/styles";
import { recruitConstant } from "services/Recruit/constant";
import "common/assets/css/text-editor.css";
import "react-quill/dist/quill.snow.css";
import { S3Helper } from "common/utils/s3-helper";
import axios from "axios";
import { toast } from "react-toastify";
import { ROLE } from "common/roles";

export default function PreviewRichText({
    html,
    title,
    pdfUrl: s3PdfUrl,
    isOpen: externalIsOpen = false,
    onClose: externalOnClose = null,
    // 'pdf' (default): show PDF preview (from S3 if provided, else generates PDF from HTML)
    // 'html': show formatted HTML content directly in the modal
    // 'auto': if pdfUrl is provided show PDF, else show HTML
    previewFormat = 'pdf',
}) {
    const contentRef = useRef(null);
    const [pdfUrl, setPdfUrl] = useState("");
    const [isOpen, setIsOpen] = useState(externalIsOpen);
    const [loading, setLoading] = useState(false);
    const [fileType, setFileType] = useState(null);
    const [blobUrl, setBlobUrl] = useState(null); // Track blob URLs for cleanup
    const colors = globalStyles.colors;
    const s3HelperInstance = S3Helper.getInstance();
    const [isEmployee, setIsEmployee] = useState(false);
    const effectiveFormat =
        previewFormat === 'auto' ? (s3PdfUrl ? 'pdf' : 'html') : previewFormat;

    useEffect(() => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                setIsEmployee(false);
                return;
            }
            const user = JSON.parse(userStr);
            let activeRoleName = null;
            if (user?.activeRoleId?.roleName) {
                activeRoleName = user.activeRoleId.roleName;
            } else if (user?.activeRoleId && Array.isArray(user?.roles)) {
                const activeRoleId = user.activeRoleId?._id || user.activeRoleId;
                const activeRole = user.roles.find((role) => {
                    const roleId = role?._id || role;
                    return String(roleId) === String(activeRoleId);
                });
                activeRoleName = activeRole?.roleName;
            }
            activeRoleName = activeRoleName || user?.role;
            setIsEmployee((activeRoleName || '').toLowerCase() === ROLE.employee);
        } catch (error) {
            console.error('Failed to resolve user role for PreviewRichText:', error);
            setIsEmployee(false);
        }
    }, []);

    // Sync internal state with external prop and auto-trigger preview
    useEffect(() => {
        if (externalIsOpen !== undefined) {
            setIsOpen(externalIsOpen);

            // When modal opens, reset state and load PDF
            if (externalIsOpen) {
                // Clean up previous blob URL if exists
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                }
                // Reset all state to ensure fresh load
                setBlobUrl(null);
                setPdfUrl("");
                setFileType(null);
                setLoading(false);

                // Small delay to ensure state is reset before loading
                const timer = setTimeout(() => {
                    if (effectiveFormat === 'pdf' && s3PdfUrl) {
                        generatePreview();
                    }
                    if (effectiveFormat === 'html') {
                        // No async loading needed, just open the modal.
                        setLoading(false);
                        setFileType('html');
                    }
                }, 100);

                return () => clearTimeout(timer);
            } else {
                // When modal closes, clean up blob URL
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                }
                setBlobUrl(null);
                setPdfUrl("");
                setFileType(null);
                setLoading(false);
            }
        }
    }, [externalIsOpen, s3PdfUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
            }
        };
    }, [blobUrl]);

    // Cleanup blob URL when modal closes
    const handleCloseModal = () => {
        setIsOpen(false);
        // Clean up blob URL
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl);
            setBlobUrl(null);
        }
        // Reset state
        setPdfUrl("");
        setFileType(null);
        setLoading(false);

        // Call external onClose if provided
        if (externalOnClose) {
            externalOnClose();
        }
    };

    // 🎯 Helper function to extract S3 key from full S3 URL
    const extractS3KeyFromUrl = (url) => {
        if (!url) return null;

        // Check if it's an S3 URL (contains .s3. or amazonaws.com)
        if (url.includes('.s3.') || url.includes('amazonaws.com')) {
            try {
                // Extract the key from URLs like:
                // https://bucket.s3.region.amazonaws.com/path/to/file.pdf
                // https://s3.region.amazonaws.com/bucket/path/to/file.pdf
                const urlWithoutParams = url.split('?')[0]; // Remove query params
                const urlParts = urlWithoutParams.split('.com/');
                if (urlParts.length > 1) {
                    return decodeURIComponent(urlParts[1]);
                }
            } catch (e) {
                console.error('Error extracting S3 key from URL:', e);
            }
        }
        return null;
    };

    // 🎯 Get PDF URL from S3 if pdfUrl is provided
    const getS3PdfUrl = async (s3KeyOrUrl) => {
        try {
            setLoading(true);

            let s3Key = s3KeyOrUrl;

            // If it's a full S3 URL, extract the S3 key from it
            // This handles the case where the backend stored a full URL instead of just the key
            if (s3KeyOrUrl && (s3KeyOrUrl.startsWith('http://') || s3KeyOrUrl.startsWith('https://'))) {
                const extractedKey = extractS3KeyFromUrl(s3KeyOrUrl);
                if (extractedKey) {
                    // It's an S3 URL - use the extracted key to get a presigned URL
                    s3Key = extractedKey;
                } else {
                    // It's not an S3 URL (maybe CloudFront or other CDN), return as-is
                    const urlPath = s3KeyOrUrl.split('?')[0];
                    const fileExtension = urlPath.split('.').pop()?.toLowerCase();
                    if (fileExtension === 'pdf') {
                        setFileType('pdf');
                    } else {
                        setFileType('other');
                    }
                    return s3KeyOrUrl;
                }
            }

            // Treat it as an S3 key and get a presigned URL
            let user = null;
            const userStr = localStorage.getItem('user');
            if (userStr) {
                user = JSON.parse(userStr);
            }

            // Extract filename from S3 key
            const fileName = s3Key.split('/').pop();
            const filePath = s3Key.substring(0, s3Key.lastIndexOf('/') + 1);

            // Detect file type from filename
            const fileExtension = fileName.split('.').pop()?.toLowerCase();
            if (fileExtension === 'pdf') {
                setFileType('pdf');
            } else if (['doc', 'docx'].includes(fileExtension)) {
                setFileType('docx');
            } else {
                setFileType('other');
            }

            // Ensure userId is a string, not an object
            const userId = user?._id ? (typeof user._id === 'object' ? user._id.toString() : String(user._id)) : 'anonymous';

            const data = [{
                fileName: fileName,
                method: "GET",
                filePath: filePath,
                userId: userId
            }];

            const urlData = await s3HelperInstance.getDownloadUrl(data);
            // getDownloadUrl returns response?.data?.data[0] which is an object with url property
            const downloadUrl = urlData?.url || (typeof urlData === 'string' ? urlData : '');
            if (!downloadUrl) {
                throw new Error('Failed to get download URL from S3');
            }
            return downloadUrl;
        } catch (error) {
            console.error("Error getting S3 PDF URL:", error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // 🎯 Generate PDF Blob + Show Preview
    const generatePreview = async () => {
        if (effectiveFormat === 'html') {
            if (!html || html.trim() === '') {
                toast.error('No content available for preview');
                return;
            }
            setFileType('html');
            setIsOpen(true);
            return;
        }

        // If S3 PDF URL/key is provided, use it directly (prefer PDF over HTML)
        if (s3PdfUrl) {
            try {
                setLoading(true);
                const downloadUrl = await getS3PdfUrl(s3PdfUrl);

                // Always fetch PDF as blob and create blob URL for iframe (avoids CORS issues)
                try {
                    const response = await axios.get(downloadUrl, {
                        responseType: 'blob',
                        headers: {
                            'Accept': 'application/pdf'
                        }
                    });

                    // Verify it's actually a PDF
                    if (response.data.type === 'application/pdf' || downloadUrl.toLowerCase().includes('.pdf')) {
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const blobUrlObj = URL.createObjectURL(blob);
                        setBlobUrl(blobUrlObj);
                        setPdfUrl(blobUrlObj);
                        setFileType('pdf'); // Ensure file type is set
                        setLoading(false); // Clear loading state
                        // Only set isOpen if not controlled externally
                        if (externalOnClose === null) {
                            setIsOpen(true);
                        }
                    } else {
                        throw new Error('File is not a PDF');
                    }
                } catch (fetchError) {
                    console.error("Error fetching PDF blob:", fetchError);
                    toast.error('Failed to load PDF. Please try downloading it.');
                    return;
                }
            } catch (error) {
                console.error("Error loading PDF from S3:", error);
                toast.error('Failed to load PDF. Please try again.');
                return;
            } finally {
                setLoading(false);
            }
            return;
        }

        // If no S3 PDF but HTML content exists, generate PDF from HTML
        if (!html || html.trim() === '') {
            toast.error('No content available for preview');
            return;
        }

        // Otherwise, generate PDF from HTML content
        const element = contentRef.current;
        if (!element || !html) {
            console.warn("PreviewRichText: No content to generate PDF", { element: !!element, html: !!html });
            return;
        }

        // Store parent container
        const parentContainer = element.parentElement;
        const originalParentStyles = {
            position: parentContainer.style.position,
            overflow: parentContainer.style.overflow,
        };

        // Store original element styles
        const originalStyles = {
            display: element.style.display,
            position: element.style.position,
            left: element.style.left,
            top: element.style.top,
            width: element.style.width,
            height: element.style.height,
            zIndex: element.style.zIndex,
            visibility: element.style.visibility,
            opacity: element.style.opacity,
        };

        // Make parent container relative if needed
        if (getComputedStyle(parentContainer).position === 'static') {
            parentContainer.style.position = 'relative';
        }
        parentContainer.style.overflow = 'hidden';

        // Make element visible but positioned off-screen for html2canvas
        element.style.display = "block";
        element.style.position = "absolute";
        element.style.left = "0";
        element.style.top = "0";
        element.style.width = "210mm"; // A4 width
        element.style.zIndex = "9999";
        element.style.visibility = "visible";
        element.style.opacity = "1";
        element.style.backgroundColor = "#fff";
        element.style.color = "#000";

        try {
            // Wait for styles to apply and content to render
            await new Promise(resolve => setTimeout(resolve, 200));

            // Verify content is actually there
            if (!element.innerHTML || element.innerHTML.trim() === '') {
                console.error("PreviewRichText: Element has no content", element.innerHTML);
                return;
            }

            const opt = {
                margin: 5,
                filename: `${title}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    windowWidth: element.scrollWidth || 794, // A4 width in pixels at 96dpi
                    windowHeight: element.scrollHeight || 1123, // A4 height in pixels
                    backgroundColor: "#ffffff",
                },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            };

            const worker = html2pdf().set(opt).from(element);

            // Generate Blob instead of saving file
            const blob = await worker.outputPdf("blob");

            const url = URL.createObjectURL(blob);
            setPdfUrl(url);
            setIsOpen(true);
        } catch (error) {
            console.error("Error generating PDF:", error);
        } finally {
            // Restore original styles
            Object.keys(originalStyles).forEach(key => {
                element.style[key] = originalStyles[key] || "";
            });
            parentContainer.style.position = originalParentStyles.position || "";
            parentContainer.style.overflow = originalParentStyles.overflow || "";
        }
    };

    // 🎯 Direct Download
    const downloadPDF = async () => {
        if (effectiveFormat !== 'pdf') return;

        // If S3 PDF URL is provided and no HTML content, download from S3
        if (s3PdfUrl && (!html || html.trim() === '')) {
            try {
                setLoading(true);
                const downloadUrl = await getS3PdfUrl(s3PdfUrl);

                // Extract filename from S3 key
                const fileName = s3PdfUrl.split('/').pop() || `${title}.pdf`;

                const response = await axios.get(downloadUrl, {
                    responseType: 'blob'
                });

                const blob = new Blob([response.data]);
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();

                setTimeout(() => {
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                }, 100);
            } catch (error) {
                console.error("Error downloading PDF from S3:", error);
            } finally {
                setLoading(false);
            }
            return;
        }

        // Otherwise, generate and download PDF from HTML content
        const element = contentRef.current;
        if (!element || !html) {
            console.warn("PreviewRichText: No content to download", { element: !!element, html: !!html });
            return;
        }

        // Store parent container
        const parentContainer = element.parentElement;
        const originalParentStyles = {
            position: parentContainer.style.position,
            overflow: parentContainer.style.overflow,
        };

        // Store original element styles
        const originalStyles = {
            display: element.style.display,
            position: element.style.position,
            left: element.style.left,
            top: element.style.top,
            width: element.style.width,
            height: element.style.height,
            zIndex: element.style.zIndex,
            visibility: element.style.visibility,
            opacity: element.style.opacity,
        };

        // Make parent container relative if needed
        if (getComputedStyle(parentContainer).position === 'static') {
            parentContainer.style.position = 'relative';
        }
        parentContainer.style.overflow = 'hidden';

        // Make element visible but positioned off-screen for html2canvas
        element.style.display = "block";
        element.style.position = "absolute";
        element.style.left = "0";
        element.style.top = "0";
        element.style.width = "210mm"; // A4 width
        element.style.zIndex = "9999";
        element.style.visibility = "visible";
        element.style.opacity = "1";
        element.style.backgroundColor = "#fff";
        element.style.color = "#000";

        try {
            // Wait for styles to apply and content to render
            await new Promise(resolve => setTimeout(resolve, 200));

            // Verify content is actually there
            if (!element.innerHTML || element.innerHTML.trim() === '') {
                console.error("PreviewRichText: Element has no content", element.innerHTML);
                return;
            }

            await html2pdf()
                .set({
                    margin: 10,
                    filename: `${title}.pdf`,
                    image: { type: "jpeg", quality: 0.98 },
                    html2canvas: {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        windowWidth: element.scrollWidth || 794,
                        windowHeight: element.scrollHeight || 1123,
                        backgroundColor: "#ffffff",
                    },
                    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
                })
                .from(element)
                .save();
        } catch (error) {
            console.error("Error downloading PDF:", error);
        } finally {
            // Restore original styles
            Object.keys(originalStyles).forEach(key => {
                element.style[key] = originalStyles[key] || "";
            });
            parentContainer.style.position = originalParentStyles.position || "";
            parentContainer.style.overflow = originalParentStyles.overflow || "";
        }
    };

    return (
        <>
            {/* Off-screen HTML Content (Used for PDF generation) */}
            <div style={{ position: "absolute", left: "-9999px", top: 0, width: "210mm", zIndex: -1 }}>
                <style>
                    {`
        .ql-align-right { text-align: right; }
        .ql-align-center { text-align: center; }
        .ql-align-justify { text-align: justify; }
        .ql-editor {
            padding: 12px 15px;
            min-height: 100px;
            font-size: 13px;
            line-height: 1.6;
            color: #000;
            background: #fff;
        }
        .ql-editor p {
            margin-bottom: 1em;
            color: #000;
        }
        .ql-editor ul, .ql-editor ol {
            margin: 1em 0;
            padding-left: 2em;
        }
        .ql-editor h1, .ql-editor h2, .ql-editor h3 {
            margin-top: 1.5em;
            margin-bottom: 0.75em;
            font-weight: bold;
            color: #000;
        }
        .ql-editor strong {
            font-weight: bold;
        }
        .ql-editor em {
            font-style: italic;
        }
        `}
                </style>

                <div
                    ref={contentRef}
                    className="ql-editor"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
                    style={{
                        padding: "2mm",
                        display: "block",
                        backgroundColor: "#fff",
                        color: "#000",
                    }}
                />
            </div>


            {/* UI Row - Only show if not controlled externally (for backward compatibility) */}
            {externalOnClose === null && (
                <Box
                    border="2px solid"
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    w="250px"
                    borderColor="#cde2f1"
                    borderRadius="md"
                    bg="white"
                >
                    <Text
                        fontSize="md"
                        fontWeight="bold"
                        color="blue.600"
                        p={2.5}
                        whiteSpace="nowrap"
                    >
                        {(() => {
                            const effectiveFormat =
                                previewFormat === 'auto' ? (s3PdfUrl ? 'pdf' : 'html') : previewFormat;
                            const shortTitle = title.length > 10 ? `${title.slice(0, 10)}...` : title;
                            return effectiveFormat === 'pdf' ? `${shortTitle}.pdf` : shortTitle;
                        })()}
                    </Text>

                    <Flex gap={3} mr={3}>
                        <Image
                            src={eyeIcon}
                            h="20px"
                            w="20px"
                            cursor="pointer"
                            onClick={generatePreview}
                            opacity={loading ? 0.5 : 1}
                            pointerEvents={loading ? 'none' : 'auto'}
                        />

                        {!isEmployee && (
                            (previewFormat === 'pdf' || (previewFormat === 'auto' && !!s3PdfUrl)) ? (
                            <Image
                                src={downloadIcon}
                                h="20px"
                                w="20px"
                                cursor="pointer"
                                onClick={downloadPDF}
                                opacity={loading ? 0.5 : 1}
                                pointerEvents={loading ? 'none' : 'auto'}
                            />
                            ) : null
                        )}
                    </Flex>
                </Box>
            )}

            {/* Preview Modal */}
            <Modal
                isOpen={isOpen}
                onClose={handleCloseModal}
                size={effectiveFormat === 'pdf' ? 'full' : '6xl'}
                closeOnOverlayClick={true}
                scrollBehavior={effectiveFormat === 'pdf' ? 'inside' : 'inside'}
            >
                <ModalOverlay />
                <ModalContent
                    maxW={effectiveFormat === 'pdf' ? '100vw' : '1100px'}
                    maxH={effectiveFormat === 'pdf' ? '100vh' : 'calc(100vh - 24px)'}
                    w={effectiveFormat === 'pdf' ? '100vw' : 'calc(100% - 24px)'}
                    m={effectiveFormat === 'pdf' ? 0 : '12px auto'}
                    borderRadius={effectiveFormat === 'pdf' ? 0 : '16px'}
                    height={effectiveFormat === 'pdf' ? '100vh' : 'auto'}
                    display="flex"
                    flexDirection="column"
                    overflow="hidden"
                >
                    <ModalHeader
                        borderBottom={`1px solid ${colors.borderColor}`}
                        pb={4}
                        flexShrink={0}
                        bg={colors.white}
                        zIndex={10}
                        position={effectiveFormat === 'pdf' ? 'relative' : 'sticky'}
                        top={0}
                    >
                        {effectiveFormat === 'pdf' ? `${title}.pdf` : title}
                    </ModalHeader>
                    <ModalCloseButton />

                    <ModalBody
                        p={0}
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                        flex="1"
                        minH={0}
                    >
                        {effectiveFormat === 'html' ? (
                            <Box
                                flex="1"
                                overflowY="auto"
                                bg="gray.50"
                                px={{ base: 3, md: 6 }}
                                py={{ base: 3, md: 5 }}
                            >
                                <Box
                                    w="100%"
                                    h="60vh"
                                    mx="auto"
                                    bg="white"
                                    border="1px solid"
                                    borderColor={colors.borderColor}
                                    borderRadius="14px"
                                    boxShadow="0 14px 40px rgba(15, 23, 42, 0.10)"
                                    overflow="hidden"
                                >
                                    <Box px={{ base: 4, md: 8 }} py={{ base: 5, md: 7 }}>
                                        <Box
                                            className="policies-editor"
                                            sx={{
                                                padding: 0,
                                                color: "#0f172a",
                                                fontSize: { base: "14px", md: "15px" },
                                                lineHeight: "1.7",
                                                "& p": { margin: "0" },
                                                "& h1": { fontSize: "24px", lineHeight: "1.25", fontWeight: 700, margin: "0" },
                                                "& h2": { fontSize: "18px", lineHeight: "1.3", fontWeight: 700, margin: "0" },
                                                "& h3": { fontSize: "15px", lineHeight: "1.35", fontWeight: 700, margin: "0" },
                                                "& ul": {
                                                    paddingLeft: "22px",
                                                    margin: "4px 0",
                                                    listStyleType: "disc",
                                                },
                                                "& ol": {
                                                    paddingLeft: "22px",
                                                    margin: "4px 0",
                                                    listStyleType: "decimal",
                                                },
                                                "& ul ul": { listStyleType: "circle", margin: "2px 0" },
                                                "& ul ul ul": { listStyleType: "square", margin: "2px 0" },
                                                "& ol ol": { listStyleType: "lower-alpha", margin: "2px 0" },
                                                "& li": { marginBottom: "2px" },
                                                "& blockquote": {
                                                    borderLeft: "3px solid #cbd5e1",
                                                    paddingLeft: "14px",
                                                    color: "#334155",
                                                    margin: "6px 0",
                                                },
                                                "& a": { color: "#285AE7", textDecoration: "underline" },
                                                "& table": {
                                                    width: "100%",
                                                    borderCollapse: "collapse",
                                                    margin: "8px 0",
                                                    tableLayout: "fixed",
                                                },
                                                "& th, & td": {
                                                    border: "1px solid #CBD5E1",
                                                    padding: "10px 12px",
                                                    verticalAlign: "top",
                                                    textAlign: "left",
                                                },
                                                "& th": {
                                                    background: "#F8FAFC",
                                                    fontWeight: 700,
                                                },
                                            }}
                                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || '') }}
                                        />

                                        {(!html || html.trim() === '') ? (
                                            <Text mt={4} color="gray.500" textAlign="center">
                                                No content available. Edit this sub-policy to add formatted text.
                                            </Text>
                                        ) : null}
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}
                        {loading && (
                            <Box textAlign="center" p={8} flex="1" display="flex" alignItems="center" justifyContent="center">
                                <Text fontSize="lg">Loading PDF...</Text>
                            </Box>
                        )}
                        {pdfUrl && !loading && (
                            <>
                                {fileType === 'pdf' || pdfUrl.toLowerCase().endsWith('.pdf') || pdfUrl.includes('application/pdf') || pdfUrl.includes('.pdf') ? (
                                    <Box
                                        flex="1"
                                        width="100%"
                                        height="100%"
                                        minH={0}
                                        overflow="hidden"
                                        bg={colors.secondaryGray[100]}
                                        display="flex"
                                        alignItems="stretch"
                                        justifyContent="stretch"
                                        p={0}
                                        position="relative"
                                    >
                                        <object
                                            data={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-fit`}
                                            type="application/pdf"
                                            width="100%"
                                            height="100%"
                                            style={{
                                                border: 'none',
                                                display: 'block',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                width: '100%',
                                                height: '100%'
                                            }}
                                            aria-label={`PDF Preview: ${title}`}
                                        >
                                            <iframe
                                                src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1&zoom=page-fit`}
                                                width="100%"
                                                height="100%"
                                                style={{
                                                    border: 'none',
                                                    display: 'block',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    width: '100%',
                                                    height: '100%'
                                                }}
                                                title={`PDF Preview: ${title}`}
                                                onLoad={() => {
                                                    setLoading(false);
                                                }}
                                            />
                                        </object>
                                    </Box>
                                ) : (
                                    <Box textAlign="center" p={8}>
                                        <Text fontSize="lg" mb={4}>
                                            This file type ({fileType || 'document'}) cannot be previewed in the browser.
                                        </Text>
                                        <Text fontSize="sm" color="gray.500" mb={4}>
                                            Please download the file to view it.
                                        </Text>
                                        <Button
                                            variant="primary"
                                            onClick={() => window.open(pdfUrl, '_blank')}
                                        >
                                            Open in New Tab
                                        </Button>
                                    </Box>
                                )}
                            </>
                        )}
                    </ModalBody>

                    <ModalFooter flexShrink={0} borderTop={`1px solid ${colors.borderColor}`}>
                        {!isEmployee && (
                            (previewFormat === 'pdf' || (previewFormat === 'auto' && !!s3PdfUrl)) ? (
                            <Button variant="primary" className="nextBtn theme-btn2" onClick={downloadPDF}>
                                {recruitConstant.download}
                            </Button>
                            ) : null
                        )}
                        <Button ml={3} className="theme-btn2 cancelBtn"
                            sx={{ textTransform: "capitalize" }} variant="outline"
                            colorScheme={colors.error["100"]}
                            size="sm" onClick={handleCloseModal}>
                            {recruitConstant.close}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}
